import React, { useState } from "react";
import { LifeBuoy } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import useAtalhoDeTeclado from "../../hooks/useAtalhoDeTeclado";

// Atalho que ninguém conhece não economiza clique nenhum. Daí a boia: "?"
// serve para quem já sabe que existe, e o botão serve para quem não sabe.
//
// `quando` diz em que situação o atalho vale. Listar um que não funciona aqui
// e agora é pior do que não listar: a pessoa tenta, não acontece nada, e passa
// a desconfiar da lista inteira.
const ATALHOS = [
  { tecla: "j", oQueFaz: "Próximo atendimento da lista" },
  { tecla: "k", oQueFaz: "Atendimento anterior" },
  { tecla: "/", oQueFaz: "Buscar na lista" },
  {
    tecla: "Alt + e",
    oQueFaz: "Resolver o atendimento aberto",
    quando: ({ ticketAberto }) => ticketAberto,
  },
  {
    tecla: "Alt + t",
    oQueFaz: "Transferir o atendimento aberto",
    quando: ({ ticketAberto }) => ticketAberto,
  },
  {
    // "/" já é a busca da LISTA; dentro da conversa a busca é outra, e sem
    // atalho próprio só se chegava nela pelo mouse.
    tecla: "Alt + f",
    oQueFaz: "Buscar na conversa aberta",
    quando: ({ ticketAberto }) => ticketAberto,
  },
  {
    // Primeiro Esc desfaz a citação e o cursor fica no campo; o seguinte é
    // que sai. Dois degraus, por isso duas linhas na lista.
    tecla: "Esc",
    oQueFaz: "Cancelar a citação da resposta",
    quando: ({ ticketAberto }) => ticketAberto,
  },
  {
    tecla: "Esc",
    oQueFaz: "Sair do campo de escrever",
    quando: ({ ticketAberto }) => ticketAberto,
  },
  {
    tecla: "Esc",
    oQueFaz: "Voltar para a lista",
    quando: ({ foco, ticketAberto }) => foco && ticketAberto,
  },
  { tecla: "?", oQueFaz: "Mostrar esta lista" },
];

// `variante`: "flutuante" é a boia sobre a lista, quando não há conversa
// aberta; "botao" é o lugar dela quando há — no cabeçalho do atendimento,
// junto das outras ações, sem roubar faixa da lateral.
const AjudaDeAtalhos = ({
  foco = false,
  ticketAberto = false,
  variante = "flutuante",
}) => {
  const [aberta, setAberta] = useState(false);

  useAtalhoDeTeclado("?", () => setAberta(true));

  const valendo = ATALHOS.filter(
    (a) => !a.quando || a.quando({ foco, ticketAberto }),
  );

  const flutuante = variante === "flutuante";

  return (
    <>
      <Button
        variant={flutuante ? "outline" : "ghost"}
        size="icon"
        onClick={() => setAberta(true)}
        title="Atalhos do teclado"
        aria-label="Atalhos do teclado"
        // Some no celular: atalho de teclado precisa de teclado. Ali a boia
        // seria um botão em cima da lista para explicar o que ninguém pode
        // usar — e o cabeçalho tem espaço demais disputado para gastar com
        // isso. Quem quiser ver a lista continua tendo Configurações.
        className={
          flutuante
            ? "absolute bottom-4 left-4 z-20 hidden h-9 w-9 rounded-full opacity-70 shadow-sm transition-opacity hover:opacity-100 lg:inline-flex"
            : "hidden shrink-0 lg:inline-flex"
        }
      >
        <LifeBuoy className="h-4 w-4" />
      </Button>

      <Dialog open={aberta} onOpenChange={setAberta}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Atalhos do teclado</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            {valendo.map((a) => (
              <div
                key={`${a.tecla}-${a.oQueFaz}`}
                className="flex items-center justify-between gap-4 text-sm"
              >
                <span className="text-muted-foreground">{a.oQueFaz}</span>
                <kbd className="rounded border bg-muted px-2 py-0.5 font-mono text-xs">
                  {a.tecla}
                </kbd>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Enquanto você escreve, as teclas valem como letra — os atalhos só
            agem fora dos campos. O que muda o atendimento pede Alt (⌥ no Mac).
          </p>
          {!ticketAberto && (
            <p className="text-xs text-muted-foreground">
              Com uma conversa aberta aparecem mais atalhos aqui.
            </p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AjudaDeAtalhos;
